import { LIBRARY_MAX_ENTRIES, LIBRARY_STORAGE_PREFIX } from './constants';
import type { LibraryEntry, LibraryKey } from '@/types/anime';

/**
 * Store nhỏ, không phụ thuộc React, giữ 3 ngăn thư viện trong localStorage.
 * Tách riêng khỏi component để:
 *  - mọi chỗ trong app thấy cùng một dữ liệu (bản gốc phải gọi lại
 *    updateLibraryCounts() + renderCurrentView() bằng tay sau mỗi thay đổi);
 *  - đồng bộ được giữa nhiều tab qua sự kiện `storage`;
 *  - dùng trực tiếp với useSyncExternalStore.
 */

export type LibraryState = Record<LibraryKey, LibraryEntry[]>;

const LIBRARY_KEYS: LibraryKey[] = ['favorites', 'watchlist', 'history'];

const storageKey = (key: LibraryKey) => `${LIBRARY_STORAGE_PREFIX}:${key}`;

function isValidEntry(value: unknown): value is LibraryEntry {
	if (!value || typeof value !== 'object') return false;
	const entry = value as Partial<LibraryEntry>;
	return typeof entry.slug === 'string' && typeof entry.title === 'string';
}

function readKey(key: LibraryKey): LibraryEntry[] {
	try {
		const raw = window.localStorage.getItem(storageKey(key));
		if (!raw) return [];
		const parsed: unknown = JSON.parse(raw);
		if (!Array.isArray(parsed)) return [];
		return parsed.filter(isValidEntry).slice(0, LIBRARY_MAX_ENTRIES);
	} catch {
		// Dữ liệu cũ hỏng hoặc localStorage bị chặn — coi như ngăn trống.
		return [];
	}
}

function readAll(): LibraryState {
	return {
		favorites: readKey('favorites'),
		watchlist: readKey('watchlist'),
		history: readKey('history'),
	};
}

let snapshot: LibraryState = readAll();
const listeners = new Set<() => void>();

/** Lý do thất bại gần nhất, để UI báo đúng khi trình duyệt chặn localStorage. */
let lastError: string | null = null;

function emit() {
	for (const listener of listeners) listener();
}

function write(key: LibraryKey, entries: LibraryEntry[]): boolean {
	const capped = entries.slice(0, LIBRARY_MAX_ENTRIES);
	try {
		window.localStorage.setItem(storageKey(key), JSON.stringify(capped));
		lastError = null;
	} catch {
		lastError = 'Trình duyệt không cho phép lưu thư viện (chế độ riêng tư?).';
		return false;
	}
	// Tạo object mới để useSyncExternalStore nhận ra thay đổi tham chiếu.
	snapshot = { ...snapshot, [key]: capped };
	emit();
	return true;
}

export function subscribe(listener: () => void): () => void {
	listeners.add(listener);
	return () => listeners.delete(listener);
}

export function getSnapshot(): LibraryState {
	return snapshot;
}

export function getLastError(): string | null {
	return lastError;
}

export function isSaved(key: LibraryKey, slug: string): boolean {
	return snapshot[key].some((entry) => entry.slug === slug);
}

/** Thêm (hoặc đưa lên đầu) một bản ghi. */
export function saveEntry(key: LibraryKey, entry: LibraryEntry): boolean {
	const rest = snapshot[key].filter((saved) => saved.slug !== entry.slug);
	return write(key, [entry, ...rest]);
}

export function removeEntry(key: LibraryKey, slug: string): boolean {
	return write(
		key,
		snapshot[key].filter((entry) => entry.slug !== slug),
	);
}

/** Trả về true nếu sau thao tác bản ghi đang được lưu. */
export function toggleEntry(key: LibraryKey, entry: LibraryEntry): boolean {
	if (isSaved(key, entry.slug)) {
		removeEntry(key, entry.slug);
		return false;
	}
	saveEntry(key, entry);
	return true;
}

/**
 * Ghi nhận lượt xem. Giữ lại tiến độ cũ nếu lần này chưa có, để việc mở lại
 * trang chi tiết không xoá mất mốc "đang xem tiếp".
 */
export function recordHistory(entry: LibraryEntry): void {
	const previous = snapshot.history.find((item) => item.slug === entry.slug);
	saveEntry('history', {
		...previous,
		...entry,
		progress: entry.progress ?? previous?.progress,
		lastEpisodeSlug: entry.lastEpisodeSlug ?? previous?.lastEpisodeSlug,
		lastEpisodeName: entry.lastEpisodeName ?? previous?.lastEpisodeName,
	});
}

export function clearLibrary(key: LibraryKey): boolean {
	return write(key, []);
}

// Đồng bộ khi người dùng mở nhiều tab NekoStream.
if (typeof window !== 'undefined') {
	window.addEventListener('storage', (event) => {
		if (!event.key) return;
		const changed = LIBRARY_KEYS.find((key) => storageKey(key) === event.key);
		if (!changed) return;
		snapshot = { ...snapshot, [changed]: readKey(changed) };
		emit();
	});
}
